/**
 * Shared-photo-link helpers (pure — unit tested):
 *  - boot param resolution: ?share= takes precedence over ?resume=
 *  - share param shape check ("<id>.<token>")
 *  - the accept-confirm predicate (only interrupt when the device's current
 *    order actually has prints to lose)
 */

/** What the app should do on boot, based on the URL. `share` wins over `resume`. */
export type BootIntent =
  | { kind: 'share'; param: string }
  | { kind: 'resume'; param: string }
  | { kind: 'none' };

export function bootIntent(params: Pick<URLSearchParams, 'get'>): BootIntent {
  const share = params.get('share');
  if (share) return { kind: 'share', param: share };
  const resume = params.get('resume');
  if (resume) return { kind: 'resume', param: resume };
  return { kind: 'none' };
}

/** True when the raw ?share= value looks like "<id>.<token>" (both halves non-empty). */
export function isValidShareParam(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const dot = raw.indexOf('.');
  return dot > 0 && dot < raw.length - 1;
}

/**
 * Show the "start a new order from these shared photos?" confirm dialog only
 * when accepting would replace an order the user actually built (has prints).
 * A project with just photos (or nothing) is replaced silently.
 */
export function shouldConfirmShareAccept(printCount: number): boolean {
  return printCount > 0;
}

/** "3 photos were shared with you." / "1 photo was shared with you." */
export function shareBannerText(photoCount: number): string {
  const noun = photoCount === 1 ? 'photo was' : 'photos were';
  return `${photoCount} ${noun} shared with you. Pick your sizes and papers.`;
}
